/* eslint-disable max-lines */
import {
  DayGroupRange,
  LocationAlarmCamera,
  LocationAlarmUpdateType,
  MotionDetectionWindow,
  QuickResolveActionItem,
  ScanAlarmUpdateType,
  Situation,
  SOPException,
  SOPNote,
  SOPTalkdownType,
  SOPWorkflow,
} from '@hakimo-ui/hakimo/types';
import { MatchedFaceWithFrame } from '@hakimo-ui/hakimo/ui-elements';
import { getPreviousDayOfWeek } from '@hakimo-ui/hakimo/util';
import dayjs from 'dayjs';
import dayjsTimezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { atom } from 'jotai';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import {
  DEFAULT_SOP_QUICK_RESOLVE,
  DEFAULT_SOP_SITUATIONS,
} from '../sop-manager/constants';
import { updateNullValues } from '../sop-manager/utils';
dayjs.extend(utc);
dayjs.extend(dayjsTimezone);

export enum EscalationStep {
  PRE_ESCALATE = 'pre_escalation',
  ESCALATION = 'escalation',
}

export enum WorkflowItemType {
  ESCALATE = 'escalate_alarm',
  RESOLVE = 'resolve_alarm',
  STEPS = 'workflow_steps',
}

export enum SOPStepType {
  STATIC_TALKDOWN = 'staticTalkdown',
  DYANMIC_TALKDOWN = 'dynamicTalkdown',
  FIRST_STEP = 'firstStep',
}
export interface DayLocationMonitoringConfig {
  timezone: string;
  dayOfWeek: string;
  ranges?: DayGroupRange[];
}

export interface SectionState {
  id: string;
  isDetached: boolean;
  position?: { top: number; left: number };
}

export interface SectionStateContextType {
  sections: Record<string, SectionState>;
  toggleSection: (sectionId: string) => void;
  updateSectionPosition: (
    sectionId: string,
    position: { top: number; left: number }
  ) => void;
}

export interface SectionStateProviderProps {
  children: ReactNode;
}

export interface DetachableSectionProps {
  sectionId: string;
  title: string;
  children: ReactNode;
  className?: string;
}

export interface FloatingModalContainerProps {
  sectionsContent: Record<string, { title: string; content: ReactNode }>;
}

// Same shape as MatchedFaceWithFrame, with timestamp tightened from optional
// to required — every UnauthorizedFace has a concrete detection time by
// construction. cameraName stays optional: the alarm APIs omit the camera
// name when the source camera is disabled, and a detection is still worth
// surfacing without it.
export type UnauthorizedFace = MatchedFaceWithFrame &
  Required<Pick<MatchedFaceWithFrame, 'timestamp'>>;

export interface GroupedFace {
  profile_id: string;
  profile_name: string;
  employee_id?: string;
  image_path: string;
  gender?: string;
  frame_url: string;
  face_box: number[];
  full_body_image_path?: string | null;
  person_box?: number[];
  detections: Array<{
    timestamp: number;
    cameraName?: string;
    // RG-only: the raw alarm this detection belongs to, so a click can
    // resolve and switch to the owning camera. Absent for Scan.
    alarmId?: string;
  }>;
}

export interface FaceImageProps {
  imagePath: string;
  profileName: string;
  employeeId?: string;
  gender?: string;
  timestamp?: number;
  cameraName?: string;
  frameUrl?: string;
  faceBox?: number[];
  fullBodyImagePath?: string | null;
  personBox?: number[];
}

export const SITUATION_OPTIONS = [
  { value: 'evidence', label: 'Evidence', dotClass: 'bg-primary-500' },
  { value: 'dispatch', label: 'Dispatch', dotClass: 'bg-status-orange' },
  { value: 'arrival', label: 'Arrival', dotClass: 'bg-status-green' },
] as const;

export const SUB_CATEGORY_OPTIONS = [
  { value: 'ACCEPTED', label: 'Accepted' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'DENIED', label: 'Denied' },
] as const;

export const RESPONDER_OPTIONS = [
  { value: 'POLICE', label: 'Police' },
  { value: 'SECURITY', label: 'Security' },
] as const;

export type UpdateType = 'dispatch' | 'arrival' | 'evidence';

export type DispatchTimelineVariant = 'compact' | 'wide';

export const BADGE_CLASS: Record<UpdateType, string> = {
  dispatch: 'bg-status-orange/15 text-status-orange',
  evidence: 'bg-primary-500/15 text-primary-400',
  arrival: 'bg-status-green/15 text-status-green',
};

export const OUTLINE_CLASS: Record<UpdateType, string> = {
  dispatch: 'outline-status-orange',
  evidence: 'outline-primary-500',
  arrival: 'outline-status-green',
};

export interface EditState {
  updateType: UpdateType;
  dispatchStatus: SubCategory | '';
  responderType: ResponderType | '';
  comment: string;
  tsIso: string | null;
  s3Urls: string[];
  pendingFiles: File[];
  pendingBlobUrls: string[];
  previewIdx: number | null;
  imagesModified: boolean;
}

export const derivedUpdateType = (t: string): UpdateType => {
  if (
    t === LocationAlarmUpdateType.ESCALATION_SNAPSHOT_COMMENT ||
    t === ScanAlarmUpdateType.COMMENT_FOR_SNAPSHOT ||
    t === 'evidence'
  )
    return 'evidence';
  if (
    t === LocationAlarmUpdateType.ARRIVAL ||
    t === ScanAlarmUpdateType.ARRIVAL
  )
    return 'arrival';
  return 'dispatch';
};

export type TopSituation = (typeof SITUATION_OPTIONS)[number]['value'];
export type SubCategory = (typeof SUB_CATEGORY_OPTIONS)[number]['value'];
export type ResponderType = (typeof RESPONDER_OPTIONS)[number]['value'];

export const currentStepAtom = atom<number>(0);
export const isSOPWorkflowEnabledAtom = atom<boolean>(true);

export type SOPStep = {
  type: SOPStepType;
  title: string;
  yesCallback?: () => void;
  noCallback?: () => void;
  actionCallback?: (selectedSituation: Situation) => void;
  exceptions?: SOPException[];
  notes?: SOPNote[];
  talkdownText?: string;
  talkdownNotes?: string[];
  situations?: Situation[];
};

export const getSOPSteps = (
  navigateToNextStep: (selectedSituation?: Situation) => void,
  navigateToResolve: () => void,
  sop: SOPWorkflow,
  escalate: (selectedSituation?: Situation) => void,
  isSpeakerMapped: boolean
): SOPStep[] => {
  const sopSteps: SOPStep[] = [
    {
      type: SOPStepType.FIRST_STEP,
      title: 'Do you see any person or moving vehicle?',
      exceptions: sop.exceptions?.length > 0 ? sop.exceptions : [],
      notes: sop?.notes?.length > 0 ? sop.notes : [],
      situations: sop.situations?.length > 0 ? sop.situations : [],
      actionCallback: navigateToNextStep,
    },
  ];

  if (
    sop.isTalkdownEnabled &&
    sop.talkdowns &&
    sop.talkdowns.length > 0 &&
    isSpeakerMapped
  ) {
    sop.talkdowns.forEach((talkdown) => {
      sopSteps.push({
        type:
          talkdown.type === SOPTalkdownType.STATIC
            ? SOPStepType.STATIC_TALKDOWN
            : SOPStepType.DYANMIC_TALKDOWN,
        title:
          talkdown.type === SOPTalkdownType.STATIC
            ? 'Did the person or vehicle move away from the premises?'
            : 'Did they move away from the premises?',
        talkdownText: talkdown.text,
        talkdownNotes:
          sop.talkdownNotes && sop.talkdownNotes.length > 0
            ? sop.talkdownNotes
            : [],
        yesCallback: navigateToResolve,
        noCallback: navigateToNextStep,
      });
    });
  }
  const lastStep = sopSteps[sopSteps.length - 1];
  if (lastStep.type === SOPStepType.FIRST_STEP) {
    lastStep.actionCallback = escalate;
  } else {
    // talkdown case
    lastStep.noCallback = escalate;
  }

  return sopSteps;
};

export enum SOPTab {
  SOP_WORKFLOW = 'sopWorkflow',
  SOP_TEXT = 'sop_text',
}

export const sopTabs = [
  { id: SOPTab.SOP_WORKFLOW, name: 'SOP Workflow' },
  { id: SOPTab.SOP_TEXT, name: 'SOP Overview' },
];

export function getLatestCameraWithTalkdown(
  cameras: LocationAlarmCamera[]
): LocationAlarmCamera | null {
  const talkdownEnabledCameras = cameras.filter((cam) => cam.isTalkdownEnabled);
  return talkdownEnabledCameras.length > 0 ? talkdownEnabledCameras[0] : null;
}

// time format is HH:MM:SS
export const convertToSeconds = (time: string) => {
  const [hours, minutes, seconds] = time.split(':').map(Number);
  return hours * 60 * 60 + minutes * 60 + (seconds ?? 0);
};

export function validateSnapshotForm(description: string): {
  isValid: boolean;
  message: string;
} {
  if (!description.trim()) {
    return { isValid: false, message: 'Caption is required!' };
  }
  return { isValid: true, message: '' };
}

export function getTodaysMonitoringConfig(
  detectionWindow: MotionDetectionWindow
): DayLocationMonitoringConfig {
  if (!detectionWindow) {
    return { timezone: 'UTC', dayOfWeek: '', ranges: undefined };
  }
  const locTimezone = detectionWindow.timezone;

  const nowInTz = dayjs().tz(locTimezone);
  const dayOfWeekToday = nowInTz.format('dddd').toUpperCase();
  const dayOfWeekYesterday = getPreviousDayOfWeek(dayOfWeekToday);

  const dayGroupRanges = detectionWindow.dayGroupRanges;
  const todayGroupRanges =
    dayGroupRanges.find((group) => group.daysOfWeekTz.includes(dayOfWeekToday))
      ?.ranges ?? [];

  todayGroupRanges.forEach((range) => {
    if (convertToSeconds(range.startAtTz) >= convertToSeconds(range.endAtTz)) {
      range.endAtTz = '23:59:59';
    }
  });

  const yesterdayGroupRanges =
    dayGroupRanges.find((group) =>
      group.daysOfWeekTz.includes(dayOfWeekYesterday)
    )?.ranges ?? [];

  yesterdayGroupRanges.forEach((range) => {
    if (convertToSeconds(range.startAtTz) >= convertToSeconds(range.endAtTz)) {
      todayGroupRanges.push({ startAtTz: '00:00:00', endAtTz: range.endAtTz });
    }
  });

  return {
    timezone: locTimezone,
    dayOfWeek: dayOfWeekToday,
    ranges: todayGroupRanges,
  };
}

export function getMonitoringConfigText(
  dayMonitoringConfig?: DayLocationMonitoringConfig
) {
  if (
    !dayMonitoringConfig ||
    !dayMonitoringConfig.ranges ||
    dayMonitoringConfig.ranges.length === 0
  ) {
    return 'No monitoring window set';
  }
  let finalText = `Monitoring Hours for ${dayMonitoringConfig.dayOfWeek} (${dayMonitoringConfig.timezone})\n\n`;

  let rangesText = '';
  dayMonitoringConfig.ranges.forEach((range) => {
    rangesText += `${range.startAtTz} - ${range.endAtTz} \n`;
  });

  finalText += rangesText;
  return finalText;
}

export function useFileHandler({ multiple = false } = {}) {
  const [fileList, setFileList] = useState<File[]>([]);
  const [previewList, setPreviewList] = useState<string[]>([]);

  useEffect(() => {
    // Clean up old previews
    previewList.forEach((url) => URL.revokeObjectURL(url));
    if (!fileList.length) {
      setPreviewList([]);
      return;
    }
    const urls = fileList.map((file) => URL.createObjectURL(file));
    setPreviewList(urls);

    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [fileList]);

  const onFilesSelect = useCallback(
    (newFiles: File[]) => {
      setFileList((prev) =>
        multiple ? [...prev, ...newFiles] : newFiles.slice(0, 1)
      );
    },
    [multiple]
  );

  const onFileRemove = useCallback((index: number) => {
    setFileList((prev) => prev.filter((_, i) => i !== index));
  }, []);

  return {
    fileList,
    previewList,
    onFilesSelect,
    onFileRemove,
    setFiles: setFileList,
  };
}

export function usePasteImages(
  setFiles: Dispatch<SetStateAction<File[]>>,
  enabled = true
) {
  useEffect(() => {
    if (!enabled) return;
    const handler = (e: ClipboardEvent) => {
      const files = Array.from(e.clipboardData?.items ?? [])
        .filter((i) => i.type.startsWith('image/'))
        .map((i) => i.getAsFile())
        .filter(Boolean) as File[];
      if (files.length) {
        e.preventDefault();
        setFiles((prev) => [...prev, ...files]);
      }
    };
    window.addEventListener('paste', handler);
    return () => window.removeEventListener('paste', handler);
  }, [setFiles, enabled]);
}

export function isDecodableImage(file: File): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(true);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(false);
    };
    img.src = url;
  });
}

export interface ImageValidationOptions {
  acceptedTypes: string[];
  maxBytes: number;
}

export async function validateImageFile(
  file: File,
  existing: File[],
  options: ImageValidationOptions
): Promise<string | null> {
  const isHeic = /\.heic$/i.test(file.name) || file.type === 'image/heic';
  if (isHeic) return "HEIC isn't supported — save as JPG or PNG.";
  if (!options.acceptedTypes.includes(file.type)) {
    return 'Only JPG, PNG and GIF images are supported.';
  }
  if (file.size > options.maxBytes) {
    const maxMB = (options.maxBytes / (1024 * 1024)).toFixed(0);
    return `'${file.name}' is too large (max ${maxMB} MB).`;
  }
  if (existing.some((f) => f.name === file.name && f.size === file.size)) {
    return 'That image is already attached.';
  }
  if (!(await isDecodableImage(file))) {
    return `'${file.name}' isn't a valid image.`;
  }
  return null;
}

export const updateDefaultValues = (workflow: SOPWorkflow) => {
  const updatedWorkflow = updateNullValues(workflow);
  if (!updatedWorkflow.talkdowns) {
    updatedWorkflow.talkdowns = [];
    updatedWorkflow.firstTalkdown &&
      updatedWorkflow.talkdowns.push(updatedWorkflow.firstTalkdown);
    updatedWorkflow.secondTalkdown &&
      updatedWorkflow.talkdowns.push(updatedWorkflow.secondTalkdown);
  }
  const situations = updatedWorkflow.situations ?? [];
  if (situations.length === 0) {
    updatedWorkflow.situations = [...DEFAULT_SOP_SITUATIONS];
  }

  if (
    !updatedWorkflow.quickResolveActions ||
    updatedWorkflow.quickResolveActions.length === 0
  ) {
    updatedWorkflow.quickResolveActions =
      DEFAULT_SOP_QUICK_RESOLVE as QuickResolveActionItem[];
  }

  return updatedWorkflow;
};
